import React from 'react'
import { Box, Flex, Text } from '@common/fundamentals'
import { Button, Chip, Divider, Tab, Tabs } from '@common/material'
import { localizedStrings } from '@core/strings'
import { SiteBoxDocument } from '@core/entities/SiteBoxDocument'
import { SiteBoxDocumentConfig } from '@core/entities/SiteBoxDocumentConfig'
import moment from 'moment'
import { Palette } from '@core/theme/color-palette-siteassist'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import { DetailRow } from './components/DetailsRow'

type SiteBoxDocumentPropertiesProps = {
  siteBoxDocument: SiteBoxDocument
  siteBoxConfiguration: SiteBoxDocumentConfig | undefined
  canBypassReview: boolean
  canUserBypassReview: boolean
  canUserAppraiseDocument: boolean
  onBypassReviewClick?: () => void
  onStartReviewClick?: () => void
  onViewAppraisalClick?: () => void
  readOnly?: boolean
}

export const SiteBoxDocumentProperties: React.FC<
  SiteBoxDocumentPropertiesProps
> = ({
  siteBoxDocument,
  siteBoxConfiguration,
  canBypassReview,
  canUserBypassReview,
  canUserAppraiseDocument,
  onBypassReviewClick,
  onStartReviewClick,
  onViewAppraisalClick,
  readOnly,
}) => {
  const formattedDate = moment(siteBoxDocument.createdOn).format(
    'DD/MM/YYYY HH:mm',
  )
  const documentType = siteBoxConfiguration?.config?.type
  const appraisalStatus = siteBoxConfiguration?.status

  const isProcessed =
    siteBoxDocument.documentIntelligence.status === 'PROCESSED'
  const inReview = siteBoxDocument.documentIntelligence.status === 'IN_REVIEW'
  const isDocumentAppraised = siteBoxConfiguration?.isDocumentAppraised()

  const reviewer = siteBoxDocument.documentIntelligence.reviewer
  const reviewDate = siteBoxDocument.documentIntelligence.reviewDate
  const formattedReviewDate = reviewDate
    ? moment(reviewDate).format('DD/MM/YY HH:mm')
    : ''

  const appraisalStatusText = siteBoxConfiguration?.getAppraisalStatusText()
  const documentEngineTemplateName =
    siteBoxDocument.documentIntelligence.config?.templates?.[0]?.name

  return (
    <Flex flexDirection='column' height='100%'>
      <Tabs
        value={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Tab label={localizedStrings.documentProperties} />
      </Tabs>

      <Box p={2} flex={1} overflow='auto'>
        <Text fontSize={14} mb={2}>
          {localizedStrings.details}
        </Text>
        <Flex flexDirection='column' gap={1}>
          <DetailRow
            label={localizedStrings.documentName}
            value={siteBoxDocument.name}
          />

          <DetailRow
            label={localizedStrings.uploadedBy}
            value={siteBoxDocument.createdBy?.getFullName()}
          />

          <DetailRow
            label={localizedStrings.uploadedDate}
            value={formattedDate}
          />

          {documentType && (
            <DetailRow
              label={localizedStrings.documentType}
              value={documentType}
            />
          )}

          <DetailRow
            label={localizedStrings.status}
            value={
              <Chip
                color={siteBoxDocument.getStatusColor()}
                label={siteBoxDocument.getStatusText()}
                size='small'
              />
            }
          />
        </Flex>
        {(isProcessed || isDocumentAppraised || inReview) && (
          <>
            <Divider sx={{ my: 3 }} />
            <Text color='text.secondary' fontSize={14} fontWeight={500} mb={2}>
              {localizedStrings.review}
            </Text>
            {isDocumentAppraised ? (
              <Box
                onClick={onViewAppraisalClick}
                sx={{
                  border: 1,
                  borderColor: Palette.Grey[300],
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: Palette.Grey[100],
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: Palette.Grey[200],
                  },
                }}
              >
                <Flex justifyContent='space-between' alignItems='flex-start'>
                  <Box>
                    <Flex alignItems='center' gap={1} mb={1}>
                      {appraisalStatus === 'PASS' ? (
                        <CheckCircleIcon
                          color='success'
                          sx={{ width: 18, height: 18 }}
                        />
                      ) : (
                        <CancelIcon
                          color='error'
                          sx={{ width: 18, height: 18 }}
                        />
                      )}
                      <Text fontSize={13}>{documentEngineTemplateName}</Text>
                    </Flex>
                    <Text fontSize={13} mb={0.5}>
                      {localizedStrings.status}: {appraisalStatus}
                    </Text>
                    <Text fontSize={13} mb={0.5} color='text.secondary'>
                      {localizedStrings.reviewedBy}: {reviewer?.getFullName()}{' '}
                      {formattedReviewDate}
                    </Text>
                    {siteBoxConfiguration?.hasExpiry() && (
                      <Text fontSize={13} color='text.secondary'>
                        {`${localizedStrings.expiryDate}: `}
                        <Text
                          component='span'
                          fontSize={13}
                          sx={{
                            color:
                              siteBoxConfiguration.getExpiryStatusTextColor() ??
                              undefined,
                          }}
                        >
                          {siteBoxConfiguration.getFormattedExpiryDate()}
                        </Text>
                      </Text>
                    )}
                  </Box>
                </Flex>
              </Box>
            ) : (
              <Box
                sx={{
                  border: 2,
                  borderColor: Palette.Amber[500],
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: Palette.Amber[50],
                  cursor: 'pointer',
                }}
                onClick={onStartReviewClick}
              >
                <Flex alignItems='center' gap={1} mb={1}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'warning.main',
                    }}
                  />
                  <Text fontSize={13}>{documentEngineTemplateName}</Text>
                </Flex>
                <Text color='text.secondary' fontSize={13} mb={1}>
                  {localizedStrings.status}: {appraisalStatusText}
                </Text>
                {readOnly ? (
                  <>
                    <Divider sx={{ mb: 2 }} />
                    <Flex gap={2}>
                      <Button
                        variant='contained'
                        onClick={(e) => {
                          e.stopPropagation()
                          onStartReviewClick?.()
                        }}
                        sx={{ flex: 1, bgcolor: Palette.Violet[600] }}
                      >
                        {localizedStrings.preview}
                      </Button>
                    </Flex>
                  </>
                ) : (
                  (canUserBypassReview || canUserAppraiseDocument) && (
                    <>
                      <Divider sx={{ mb: 2 }} />
                      <Flex gap={2}>
                        {canBypassReview && canUserBypassReview && (
                          <Button
                            variant='contained'
                            onClick={(e) => {
                              e.stopPropagation()
                              onBypassReviewClick?.()
                            }}
                            sx={{ flex: 1, bgcolor: Palette.Violet[500] }}
                          >
                            {localizedStrings.bypassReview}
                          </Button>
                        )}
                        {canUserAppraiseDocument && (
                          <Button
                            variant='contained'
                            onClick={(e) => {
                              e.stopPropagation()
                              onStartReviewClick?.()
                            }}
                            sx={{ flex: 1, bgcolor: Palette.Violet[600] }}
                          >
                            {inReview
                              ? localizedStrings.continueReview
                              : localizedStrings.startReview}
                          </Button>
                        )}
                      </Flex>
                    </>
                  )
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Flex>
  )
}
